import { FormattedMessage } from "react-intl";

import { Alert, AlertTitle, Button } from "@mui/material";

import * as Sentry from "@sentry/react";

/**
 * Fallback for a route that failed to render. Deliberately modest: it sits
 * inside the layout, so the header, sidebar and navigation around it are still
 * usable and the user can leave without reloading.
 */
function RouteFallback({ resetError }) {
  return (
    <div className="d-flex justify-content-center mt-40">
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={resetError}>
            <FormattedMessage id="ERROR_BOUNDARY.RETRY" />
          </Button>
        }
      >
        <AlertTitle>
          <FormattedMessage id="ERROR_BOUNDARY.ROUTE.TITLE" />
        </AlertTitle>
        <FormattedMessage id="ERROR_BOUNDARY.ROUTE.DESCRIPTION" />
      </Alert>
    </div>
  );
}

/**
 * Fallback for a single item in a list. Keeps the surrounding list intact so one
 * malformed record does not take the whole page down with it.
 */
function ItemFallback({ resetError }) {
  return (
    <Alert
      severity="warning"
      className="mb-10px"
      action={
        <Button color="inherit" size="small" onClick={resetError}>
          <FormattedMessage id="ERROR_BOUNDARY.RETRY" />
        </Button>
      }
    >
      <FormattedMessage id="ERROR_BOUNDARY.ITEM.TITLE" />
    </Alert>
  );
}

/**
 * Wraps a route's content. Pass `resetKey` (normally the pathname) so navigating
 * away from a broken route clears the error instead of leaving the fallback
 * pinned in place — React error boundaries do not reset on their own.
 */
export function RouteErrorBoundary({ children, resetKey }) {
  return (
    <Sentry.ErrorBoundary key={resetKey} fallback={RouteFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
}

/** Wraps one item in a list. See ItemFallback. */
export function ItemErrorBoundary({ children, resetKey }) {
  return (
    <Sentry.ErrorBoundary key={resetKey} fallback={ItemFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
